import * as amplitude from '@amplitude/analytics-browser';

import { noopCallback } from '@looko-corp/acloset-react-kit';
import { acError, acInfo } from '@looko-corp/acloset-react-kit/src/sentry';

import type { AnalyticsServiceInterface } from './analytics-service-interface';

/**
 * Amplitude 관리 서비스.
 */
export class AmplitudeService implements AnalyticsServiceInterface {
  private instance?: ReturnType<typeof amplitude.createInstance>;

  /**
   * Amplitude 초기화.
   */
  public async load(userID?: string) {
    try {
      if (this.instance) {
        return;
      }

      this.instance = amplitude.createInstance();
      await this.instance.init(
        import.meta.env.VITE_AMPLITUDE_API_KEY,
        userID != null ? userID.padStart(5, '0') : userID,
        {
          autocapture: {
            attribution: true,
            fileDownloads: false,
            formInteractions: false,
            pageViews: true,
            sessions: true,
            elementInteractions: false,
          },
          plan: {
            source: 'looko_ai_web',
          },
        },
      ).promise;

      acInfo('Amplitude initialized');
    } catch (err) {
      acError(`Failed to initialize Amplitude : ${err}`, err);
    }
  }

  public setUser(userID: number | string | undefined) {
    // XXX: reset 하면, 완전히 새 유저로 트래킹합니다.
    // ampli.client.reset();
    this.instance?.setUserId(userID != null ? userID.toString().padStart(5, '0') : undefined);
  }

  public setUserProperties(properties: Record<string, any>) {
    const amplitudeIdentify = new amplitude.Identify();
    for (const [key, value] of Object.entries(properties)) {
      amplitudeIdentify.set(key, value);
    }

    this.instance?.identify(amplitudeIdentify).promise.catch(noopCallback);
  }

  public log(name: string, params?: Record<string, any>) {
    void this.instance
      ?.track({
        event_type: name,
        event_properties: params,
      })
      .promise.catch(noopCallback);
  }

  public logLogin(params?: Record<string, any>) {
    this.log('LOG_IN', params);
  }

  public logSignUp(params?: Record<string, any>) {
    this.log('SIGN_UP', params);
  }

  public logScreenView(params?: Record<string, any>) {
    this.log('CHANGE_SCREEN', {
      ...params,
      screen_name: params?.screen_name ?? '화면 이름 없음',
    });
  }
}
